import React, { useEffect, useRef, useState } from 'react';
import { loginWithDiscord } from '../lib/api';

const Termos = () => {
  const cursorRef = useRef(null);
  const particlesCanvasRef = useRef(null);
  const lightningCanvasRef = useRef(null);
  const btnRef = useRef(null);
  const progressBarRef = useRef(null);
  const mainContainerRef = useRef(null);
  const [chaosMode, setChaosMode] = useState(false);
  const [showFinalMessage, setShowFinalMessage] = useState(false);
  const particlesRef = useRef([]);
  const holdTimerRef = useRef(null);
  const progressRef = useRef(0);
  const animationFrameRef = useRef(null);
  const lightningIntervalRef = useRef(null);
  const chaosIntervalRef = useRef(null);
  const droneAudioRef = useRef(null);
  const thunderAudioRef = useRef(null);

  useEffect(() => {
    // Cursor & Parallax
    const cursor = cursorRef.current;
    const btn = btnRef.current;

    const handleMouseMove = (e) => {
      if (cursor) {
        cursor.style.left = e.clientX + 'px';
        cursor.style.top = e.clientY + 'px';
      }

      // Efeito Parallax (só aplica se o modo caos NÃO estiver ativo)
      if (!chaosMode) {
        document.querySelectorAll('.layer').forEach(layer => {
          const speed = layer.getAttribute('data-speed');
          const x = (window.innerWidth - e.pageX * speed) / 100;
          const y = (window.innerHeight - e.pageY * speed) / 100;
          layer.style.transform = `translateX(${x}px) translateY(${y}px)`;
        });
      }
    };

    const handleBtnMouseEnter = () => {
      if (cursor) cursor.classList.add('hovered');
    };

    const handleBtnMouseLeave = () => {
      if (cursor) cursor.classList.remove('hovered');
    };

    document.addEventListener('mousemove', handleMouseMove);
    if (btn) {
      btn.addEventListener('mouseenter', handleBtnMouseEnter);
      btn.addEventListener('mouseleave', handleBtnMouseLeave);
    }

    // Particles
    const pCanvas = particlesCanvasRef.current;
    const pCtx = pCanvas?.getContext('2d');
    let pWidth, pHeight;

    const resizeParticles = () => {
      if (pCanvas) {
        pWidth = pCanvas.width = window.innerWidth;
        pHeight = pCanvas.height = window.innerHeight;
      }
    };
    window.addEventListener('resize', resizeParticles);
    resizeParticles();

    class Particle {
      constructor() {
        this.reset();
      }
      reset() {
        this.x = Math.random() * pWidth;
        this.y = Math.random() * pHeight;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 2;
        this.alpha = Math.random() * 0.5 + 0.1;
        this.color = 'rgba(200, 200, 200,';
      }
      update() {
        const speedMult = chaosMode ? 15 : 1;
        this.x += this.vx * speedMult;
        this.y += this.vy * speedMult;

        if (chaosMode) {
          this.x += (Math.random() - 0.5) * 5;
          this.y += (Math.random() - 0.5) * 5;
        }

        if (this.x < 0 || this.x > pWidth || this.y < 0 || this.y > pHeight) {
          this.reset();
        }
      }
      draw(ctx) {
        const colorStr = chaosMode
          ? `rgba(255, 0, 0, ${this.alpha})`
          : `${this.color} ${this.alpha})`;
        ctx.fillStyle = colorStr;
        ctx.beginPath();
        ctx.arc(
          this.x,
          this.y,
          chaosMode ? this.size * 1.5 : this.size,
          0,
          Math.PI * 2
        );
        ctx.fill();
      }
    }

    for (let i = 0; i < 100; i++) {
      particlesRef.current.push(new Particle());
    }

    const animateParticles = () => {
      if (pCtx && pCanvas) {
        pCtx.clearRect(0, 0, pWidth, pHeight);
        particlesRef.current.forEach(p => {
          p.update();
          p.draw(pCtx);
        });
        animationFrameRef.current = requestAnimationFrame(animateParticles);
      }
    };
    animateParticles();

    // Lightning
    const lCanvas = lightningCanvasRef.current;
    const lCtx = lCanvas?.getContext('2d');

    const drawBolt = (x1, y1, x2, y2, displace) => {
      if (!lCtx) return;
      if (displace < 20) {
        lCtx.beginPath();
        lCtx.moveTo(x1, y1);
        lCtx.lineTo(x2, y2);
        lCtx.stroke();
        return;
      }
      const midX = (x1 + x2) / 2 + (Math.random() - 0.5) * displace;
      const midY = (y1 + y2) / 2 + (Math.random() - 0.5) * displace;
      drawBolt(x1, y1, midX, midY, displace / 2);
      drawBolt(x2, y2, midX, midY, displace / 2);
    };

    const flashLightning = (intensity = 'normal') => {
      if (!lCanvas || !lCtx) return;
      lCanvas.width = window.innerWidth;
      lCanvas.height = window.innerHeight;

      if (intensity === 'chaos') {
        lCtx.strokeStyle = 'rgba(255, 50, 50, 0.9)';
        lCtx.shadowColor = '#ff0000';
        lCtx.lineWidth = 3;
      } else {
        lCtx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        lCtx.shadowColor = '#ffffff';
        lCtx.lineWidth = 2;
      }
      lCtx.shadowBlur = 30;

      const startX = Math.random() * lCanvas.width;
      drawBolt(startX, 0, Math.random() * lCanvas.width, lCanvas.height, 300);

      const flashColor = intensity === 'chaos' ? '#330000' : '#222';
      document.body.style.backgroundColor = flashColor;

      setTimeout(() => {
        document.body.style.backgroundColor = '#050505';
      }, 50);
      setTimeout(() => {
        if (lCtx) lCtx.clearRect(0, 0, lCanvas.width, lCanvas.height);
      }, 100);
    };

    lightningIntervalRef.current = setInterval(() => {
      if (!chaosMode && Math.random() > 0.9) {
        flashLightning();
      }
    }, 2000);

    // Audio
    droneAudioRef.current = new Audio(
      'https://actions.google.com/sounds/v1/horror/horror_ambience.ogg'
    );
    droneAudioRef.current.loop = true;

    thunderAudioRef.current = new Audio(
      'https://actions.google.com/sounds/v1/weather/thunder_crack.ogg'
    );

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', resizeParticles);
      if (btn) {
        btn.removeEventListener('mouseenter', handleBtnMouseEnter);
        btn.removeEventListener('mouseleave', handleBtnMouseLeave);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (lightningIntervalRef.current) {
        clearInterval(lightningIntervalRef.current);
      }
      if (chaosIntervalRef.current) {
        clearInterval(chaosIntervalRef.current);
      }
      if (holdTimerRef.current) {
        clearInterval(holdTimerRef.current);
      }
      if (droneAudioRef.current) {
        droneAudioRef.current.pause();
        droneAudioRef.current = null;
      }
    };
  }, [chaosMode]);

  const startHold = () => {
    if (chaosMode) return;

    if (droneAudioRef.current) {
      droneAudioRef.current.play().catch(() => {});
    }

    if (btnRef.current) {
      btnRef.current.classList.add('shaking');
    }

    holdTimerRef.current = setInterval(() => {
      progressRef.current += 1.5;
      if (progressBarRef.current) {
        progressBarRef.current.style.width = progressRef.current + '%';
      }

      // Shake leve
      document.body.style.transform = `translate(${Math.random() * 2}px, ${Math.random() * 2}px)`;

      if (progressRef.current >= 100) {
        triggerChaos();
      }
    }, 20);
  };

  const cancelHold = () => {
    if (chaosMode) return;
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    progressRef.current = 0;
    if (progressBarRef.current) {
      progressBarRef.current.style.width = '0%';
    }
    if (btnRef.current) {
      btnRef.current.classList.remove('shaking');
    }
    document.body.style.transform = 'none';
  };

  const triggerChaos = () => {
    if (holdTimerRef.current) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    setChaosMode(true);

    // Esconde container principal
    if (mainContainerRef.current) {
      mainContainerRef.current.style.opacity = '0';
    }
    if (cursorRef.current) {
      cursorRef.current.style.display = 'none';
    }

    // Mostra mensagem final
    setShowFinalMessage(true);

    // Ativa shake total
    document.body.classList.add('body-shake');

    // Som
    if (thunderAudioRef.current) {
      thunderAudioRef.current.volume = 1.0;
      thunderAudioRef.current.play().catch(() => {});
    }

    // Loop Caos
    const lCanvas = lightningCanvasRef.current;
    const lCtx = lCanvas?.getContext('2d');

    const drawBolt = (x1, y1, x2, y2, displace) => {
      if (!lCtx) return;
      if (displace < 20) {
        lCtx.beginPath();
        lCtx.moveTo(x1, y1);
        lCtx.lineTo(x2, y2);
        lCtx.stroke();
        return;
      }
      const midX = (x1 + x2) / 2 + (Math.random() - 0.5) * displace;
      const midY = (y1 + y2) / 2 + (Math.random() - 0.5) * displace;
      drawBolt(x1, y1, midX, midY, displace / 2);
      drawBolt(x2, y2, midX, midY, displace / 2);
    };

    const flashChaosLightning = () => {
      if (!lCanvas || !lCtx) return;
      lCanvas.width = window.innerWidth;
      lCanvas.height = window.innerHeight;
      lCtx.strokeStyle = 'rgba(255, 50, 50, 0.9)';
      lCtx.shadowColor = '#ff0000';
      lCtx.lineWidth = 3;
      lCtx.shadowBlur = 30;

      const startX = Math.random() * lCanvas.width;
      drawBolt(startX, 0, Math.random() * lCanvas.width, lCanvas.height, 300);

      document.body.style.backgroundColor = '#330000';
      setTimeout(() => {
        document.body.style.backgroundColor = '#050505';
      }, 50);
      setTimeout(() => {
        if (lCtx) lCtx.clearRect(0, 0, lCanvas.width, lCanvas.height);
      }, 100);
    };

    chaosIntervalRef.current = setInterval(() => {
      flashChaosLightning();
      if (Math.random() > 0.5 && thunderAudioRef.current) {
        const clone = thunderAudioRef.current.cloneNode();
        clone.volume = 0.5;
        clone.play().catch(() => {});
      }
    }, 200);

    // Após 3 segundos, redirecionar para login do Discord
    setTimeout(async () => {
      try {
        await loginWithDiscord();
      } catch (error) {
        console.error('Erro ao fazer login com Discord:', error);
      }
    }, 3000);
  };

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@700&family=Montserrat:wght@900&family=Rubik+Glitch&display=swap"
        rel="stylesheet"
      />

      <style>{`
        :root {
          --primary-red: #ff0000;
          --blood-red: #8a0000;
          --bg-black: #050505;
        }

        body, html {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          background-color: var(--bg-black);
          overflow: hidden;
          font-family: 'Cinzel', serif;
          cursor: none;
          user-select: none;
        }

        .crt-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: linear-gradient(rgba(18, 16, 16, 0) 50%, rgba(0, 0, 0, 0.25) 50%), linear-gradient(90deg, rgba(255, 0, 0, 0.06), rgba(0, 255, 0, 0.02), rgba(0, 0, 255, 0.06));
          background-size: 100% 2px, 3px 100%;
          z-index: 100;
          pointer-events: none;
        }

        .vignette {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle, transparent 50%, black 120%);
          z-index: 99;
          pointer-events: none;
        }

        #cursor {
          position: fixed;
          width: 20px;
          height: 20px;
          border: 2px solid var(--primary-red);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          z-index: 1000;
          transition: width 0.2s, height 0.2s;
          box-shadow: 0 0 10px var(--primary-red);
        }

        #cursor.hovered {
          width: 50px;
          height: 50px;
          background: rgba(255, 0, 0, 0.1);
        }

        .layer {
          position: absolute;
          top: -10%;
          left: -10%;
          width: 120%;
          height: 120%;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: transform 0.1s ease-out;
          pointer-events: none;
        }

        .fog-img {
          position: absolute;
          width: 300%;
          height: 100%;
          background: url('https://raw.githubusercontent.com/danielstuart14/CSS_FOG_ANIMATION/master/fog1.png') repeat-x;
          background-size: contain;
          animation: fogMove 60s linear infinite;
          opacity: 0.3;
        }

        @keyframes fogMove {
          0% { background-position: 0 0; }
          100% { background-position: -200vw 0; }
        }

        .container {
          position: relative;
          z-index: 50;
          text-align: center;
          max-width: 800px;
          padding: 2rem;
          pointer-events: auto;
          transition: opacity 0.5s;
        }

        h1 {
          font-family: 'Montserrat', sans-serif;
          font-weight: 900;
          font-size: 3rem;
          text-transform: uppercase;
          color: #e0e0e0;
          text-shadow: 2px 2px 0px #ff0000;
          margin-bottom: 3rem;
          position: relative;
        }

        h1::before {
          content: attr(data-text);
          position: absolute;
          left: 2px;
          text-shadow: -1px 0 red;
          top: 0;
          color: white;
          background: var(--bg-black);
          overflow: hidden;
          clip: rect(0, 900px, 0, 0);
          animation: glitch-anim 3s infinite linear alternate-reverse;
        }

        @keyframes glitch-anim {
          0% { clip: rect(42px, 9999px, 44px, 0); transform: skew(0.5deg); }
          5% { clip: rect(12px, 9999px, 50px, 0); transform: skew(0.5deg); }
          10% { clip: rect(80px, 9999px, 10px, 0); transform: skew(-0.5deg); }
          15% { clip: rect(0, 0, 0, 0); }
          100% { clip: rect(0, 0, 0, 0); }
        }

        p.warning {
          color: #888;
          font-size: 0.9rem;
          letter-spacing: 2px;
          margin-bottom: 40px;
        }

        .hold-btn-container {
          position: relative;
          display: inline-block;
        }

        .hold-btn {
          background: transparent;
          color: var(--primary-red);
          font-family: 'Cinzel', serif;
          font-size: 1.5rem;
          padding: 20px 60px;
          border: 2px solid var(--primary-red);
          cursor: none;
          text-transform: uppercase;
          letter-spacing: 4px;
          position: relative;
          overflow: hidden;
        }

        .hold-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 100%;
          width: 0%;
          background-color: var(--primary-red);
          z-index: -1;
          transition: width 0.1s linear;
        }

        .hold-btn.shaking {
          animation: shake-hard 0.1s infinite;
          color: black;
        }

        @keyframes shake-hard {
          0% { transform: translate(1px, 1px) rotate(0deg); }
          25% { transform: translate(-3px, 0px) rotate(1deg); }
          50% { transform: translate(-1px, 2px) rotate(-1deg); }
          75% { transform: translate(3px, 1px) rotate(-1deg); }
          100% { transform: translate(1px, -2px) rotate(-1deg); }
        }

        #lightning-canvas, #particles-canvas {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        #lightning-canvas {
          z-index: 20;
          mix-blend-mode: screen;
        }

        #particles-canvas {
          z-index: 10;
        }

        #final-message {
          display: ${showFinalMessage ? 'flex' : 'none'};
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 200;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          background: rgba(0,0,0,0.7);
        }

        .chaos-text {
          font-family: 'Rubik Glitch', cursive;
          color: #ff0000;
          font-size: 5rem;
          text-align: center;
          text-shadow: 0 0 20px red, 4px 4px 0px black;
          animation: zoom-pulse 0.2s infinite;
          z-index: 201;
        }

        .sub-chaos {
          color: white;
          font-family: monospace;
          font-size: 1.5rem;
          margin-top: 20px;
          background: black;
          padding: 5px 10px;
          animation: blink-fast 0.1s infinite;
        }

        @keyframes zoom-pulse {
          0% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.1) rotate(-2deg); }
          100% { transform: scale(0.9) rotate(2deg); }
        }

        @keyframes blink-fast {
          0% { opacity: 1; }
          50% { opacity: 0; }
          100% { opacity: 1; }
        }

        .body-shake {
          animation: screen-shake 0.5s infinite;
        }

        @keyframes screen-shake {
          0% { transform: translate(0,0); }
          20% { transform: translate(-10px, 10px); }
          40% { transform: translate(10px, -10px); }
          60% { transform: translate(-5px, 0px); }
          80% { transform: translate(5px, 5px); }
          100% { transform: translate(0,0); }
        }
      `}</style>

      <div className="crt-overlay"></div>
      <div className="vignette"></div>
      <div id="cursor" ref={cursorRef}></div>

      <canvas id="particles-canvas" ref={particlesCanvasRef}></canvas>
      <canvas id="lightning-canvas" ref={lightningCanvasRef}></canvas>

      {/* Camada 1: Neblina (Move devagar) */}
      <div className="layer" data-speed="2">
        <div className="fog-img"></div>
      </div>

      {/* Camada 2: Conteúdo Principal (Move mais rápido contra o mouse) */}
      <div className="layer" data-speed="5">
        <div className="container" id="main-container" ref={mainContainerRef}>
          <h1 data-text="OS TERMOS SÃO ETERNOS">OS TERMOS SÃO ETERNOS</h1>
          <p className="warning">AO REGISTRAR, VOCÊ ACEITA VENDER A SUA ALMA PARA O SERVIDOR</p>

          <div className="hold-btn-container">
            <button
              ref={btnRef}
              className="hold-btn"
              id="soul-btn"
              onMouseDown={startHold}
              onMouseUp={cancelHold}
              onMouseLeave={cancelHold}
              onTouchStart={startHold}
              onTouchEnd={cancelHold}
            >
              <div className="hold-progress" id="progress-bar" ref={progressBarRef}></div>
              <span id="btn-text">SEGURE PARA ACEITAR</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tela Final */}
      <div id="final-message">
        <h2 className="chaos-text">ALMA COLETADA</h2>
        <p className="sub-chaos">NÃO DESLIGUE O SISTEMA</p>
      </div>
    </>
  );
};

export default Termos;
